import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { ChapterSummary } from '../../api/learnApi';

interface UnavailableChaptersNoticeProps {
  /** Chapters whose content could not be read; nothing renders when empty. */
  chapters: ChapterSummary[];
}

/**
 * Feature 022 (FR-044) — discloses that the searched corpus is incomplete.
 *
 * Shown as soon as the index is known to be partial, before any search is run: incomplete coverage is
 * a fact about the book, not about a given result set. A learner who searches once, finds nothing and
 * leaves must not do so believing they searched the whole book — silently omitting a chapter would
 * look complete while leading them to conclude an auction is absent.
 *
 * Amber, not red, and `role="status"` rather than `role="alert"`: search still works, so this is
 * context to be picked up politely, not an error interrupting the learner mid-interaction.
 */
export function UnavailableChaptersNotice({ chapters }: UnavailableChaptersNoticeProps) {
  if (chapters.length === 0) return null;

  const names = chapters.map((c) => `Chapter ${c.order} - ${c.title}`).join(', ');

  return (
    <div
      role="status"
      data-testid="unavailable-chapters-notice"
      className="mb-4 flex items-start gap-2 rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-900"
    >
      <ExclamationTriangleIcon className="h-5 w-5 flex-none text-amber-700" aria-hidden="true" />
      <p>
        Some notes couldn&apos;t be loaded, so results may be incomplete.{' '}
        <span className="font-medium">
          {chapters.length === 1 ? 'Missing: ' : 'Missing chapters: '}
          {names}
        </span>
        .
      </p>
    </div>
  );
}
